import React, { useState, useRef, useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogContent, DialogActions,
  Button, Typography, IconButton, Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CropIcon from '@material-ui/icons/Crop';

const HANDLE_SIZE = 10;

const useStyles = makeStyles(() => ({
  paper: {
    maxWidth: 900,
    width: '90vw',
    maxHeight: '85vh',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    borderBottom: '1px solid #e0e0e0',
  },
  content: {
    padding: 0,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 400,
  },
  instructions: {
    padding: '8px 16px',
    background: '#EEF2FF',
    fontSize: 12,
    color: '#4F46E5',
    textAlign: 'center',
    borderBottom: '1px solid #C7D2FE',
  },
  scrollWrap: {
    flex: 1,
    overflow: 'auto',
    background: '#f5f5f5',
    padding: 8,
  },
  imageContainer: {
    position: 'relative',
    display: 'inline-block',
    cursor: 'crosshair',
  },
  image: {
    display: 'block',
    maxWidth: '100%',
    userSelect: 'none',
  },
  selectionBox: {
    position: 'absolute',
    border: '2px solid #1976d2',
    background: 'rgba(25, 118, 210, 0.08)',
    zIndex: 10,
    cursor: 'move',
    pointerEvents: 'none',
  },
  handle: {
    position: 'absolute',
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
    background: '#1976d2',
    border: '1px solid #fff',
    borderRadius: 2,
    zIndex: 11,
    pointerEvents: 'auto',
  },
  previewSection: {
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#fafafa',
  },
  previewImg: {
    width: 60,
    height: 60,
    objectFit: 'contain',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    background: '#fff',
  },
}));

const SymbolPickerModal = ({ open, onClose, onSave, imageUrl, itemName }) => {
  const classes = useStyles();
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [dragMode, setDragMode] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [dragRectStart, setDragRectStart] = useState(null);
  const [croppedDataUrl, setCroppedDataUrl] = useState(null);

  useEffect(() => {
    if (open) {
      setRect(null);
      setCroppedDataUrl(null);
      setDragMode(null);
    }
  }, [open]);

  // Get mouse position relative to the image element
  const getRelPos = useCallback((e) => {
    const img = imageRef.current;
    if (!img) return null;
    const r = img.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(e.clientX - r.left, img.clientWidth)),
      y: Math.max(0, Math.min(e.clientY - r.top, img.clientHeight)),
    };
  }, []);

  const doCrop = useCallback((r) => {
    const img = imageRef.current;
    if (!img || !r || r.w < 3 || r.h < 3) { setCroppedDataUrl(null); return; }
    const sx = img.naturalWidth / img.clientWidth;
    const sy = img.naturalHeight / img.clientHeight;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(r.w * sx);
    canvas.height = Math.round(r.h * sy);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, r.x * sx, r.y * sy, r.w * sx, r.h * sy, 0, 0, canvas.width, canvas.height);
    setCroppedDataUrl(canvas.toDataURL('image/png'));
  }, []);

  const startDrag = useCallback((mode, e) => {
    const pos = getRelPos(e);
    if (!pos) return;
    setDragMode(mode);
    setDragStart(pos);
    setDragRectStart(rect ? { ...rect } : null);
  }, [getRelPos, rect]);

  const handleContainerMouseDown = useCallback((e) => {
    const pos = getRelPos(e);
    if (!pos) return;

    // If rect exists, check if click is inside (move)
    if (rect) {
      if (pos.x >= rect.x && pos.x <= rect.x + rect.w &&
          pos.y >= rect.y && pos.y <= rect.y + rect.h) {
        startDrag('move', e);
        return;
      }
    }

    // Start new selection
    setDragMode('create');
    setDragStart(pos);
    setDragRectStart(null);
    setRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
    setCroppedDataUrl(null);
  }, [rect, getRelPos, startDrag]);

  const handleMouseMove = useCallback((e) => {
    if (!dragMode || !dragStart) return;
    const pos = getRelPos(e);
    if (!pos) return;
    const dx = pos.x - dragStart.x;
    const dy = pos.y - dragStart.y;

    if (dragMode === 'create') {
      setRect({
        x: Math.min(dragStart.x, pos.x),
        y: Math.min(dragStart.y, pos.y),
        w: Math.abs(pos.x - dragStart.x),
        h: Math.abs(pos.y - dragStart.y),
      });
    } else if (dragMode === 'move' && dragRectStart) {
      setRect({ ...dragRectStart, x: dragRectStart.x + dx, y: dragRectStart.y + dy });
    } else if (dragRectStart) {
      const r = { ...dragRectStart };
      if (dragMode.includes('w')) { r.x += dx; r.w -= dx; }
      if (dragMode.includes('e')) { r.w += dx; }
      if (dragMode.includes('n')) { r.y += dy; r.h -= dy; }
      if (dragMode.includes('s')) { r.h += dy; }
      if (r.w < 5) r.w = 5;
      if (r.h < 5) r.h = 5;
      setRect(r);
    }
  }, [dragMode, dragStart, dragRectStart, getRelPos]);

  const handleMouseUp = useCallback(() => {
    if (dragMode && rect && rect.w > 3 && rect.h > 3) {
      doCrop(rect);
    }
    setDragMode(null);
    setDragStart(null);
    setDragRectStart(null);
  }, [dragMode, rect, doCrop]);

  const hs = HANDLE_SIZE / 2;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={classes.title}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <CropIcon style={{ fontSize: 20, color: '#666' }} />
          <Typography style={{ fontSize: 15, fontWeight: 500 }}>
            Select Symbol — {itemName || 'Item'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        <div className={classes.instructions}>
          Drag a rectangle around the symbol. Use corner handles to refine.
        </div>

        <div className={classes.scrollWrap}>
          <div
            ref={containerRef}
            className={classes.imageContainer}
            onMouseDown={handleContainerMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => { if (dragMode) handleMouseUp(); }}
          >
            {imageUrl && (
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Legend"
                className={classes.image}
                crossOrigin="anonymous"
                draggable={false}
              />
            )}

            {rect && rect.w > 0 && rect.h > 0 && (
              <>
                <div
                  className={classes.selectionBox}
                  style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
                />
                {[
                  { key: 'nw', x: rect.x - hs, y: rect.y - hs, cursor: 'nw-resize' },
                  { key: 'ne', x: rect.x + rect.w - hs, y: rect.y - hs, cursor: 'ne-resize' },
                  { key: 'sw', x: rect.x - hs, y: rect.y + rect.h - hs, cursor: 'sw-resize' },
                  { key: 'se', x: rect.x + rect.w - hs, y: rect.y + rect.h - hs, cursor: 'se-resize' },
                ].map(h => (
                  <div
                    key={h.key}
                    className={classes.handle}
                    style={{ left: h.x, top: h.y, cursor: h.cursor }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      startDrag(h.key, e);
                    }}
                  />
                ))}
              </>
            )}
          </div>
        </div>

        {croppedDataUrl && (
          <div className={classes.previewSection}>
            <img src={croppedDataUrl} alt="Cropped symbol" className={classes.previewImg} />
            <div>
              <Typography style={{ fontSize: 13, fontWeight: 500 }}>Symbol preview</Typography>
              <Typography variant="caption" color="textSecondary">
                This will be shown next to the item when active
              </Typography>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '8px 16px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!croppedDataUrl}
          onClick={() => { if (croppedDataUrl) onSave(croppedDataUrl); }}
        >
          Save Symbol
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SymbolPickerModal;
